package com.fantasytenis.shared.domain.bracket.entity

import kotlinx.serialization.Serializable

/**
 * Tournament round names
 */
enum class RoundName(val displayName: String, val basePoints: Int) {
    R128("Round of 128", 1),
    R64("Round of 64", 1),
    R32("Round of 32", 2),
    R16("Round of 16", 4),
    QF("Quarterfinals", 8),
    SF("Semifinals", 16),
    F("Final", 32);

    companion object {
        fun fromString(value: String): RoundName? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }

        fun fromApiValue(value: Int): RoundName? {
            return when (value) {
                1 -> R128
                2 -> R64
                3 -> R32
                4 -> R16
                5 -> QF
                6 -> SF
                7 -> F
                else -> null
            }
        }

        /**
         * Returns the list of rounds for a given draw size.
         * @param drawSize The tournament draw size (16, 32, 64, or 128)
         * @return List of RoundName values in order from first round to final
         */
        fun getRoundsForDrawSize(drawSize: Int): List<RoundName> {
            return when (drawSize) {
                128 -> listOf(R128, R64, R32, R16, QF, SF, F)
                64 -> listOf(R64, R32, R16, QF, SF, F)
                32 -> listOf(R32, R16, QF, SF, F)
                16 -> listOf(R16, QF, SF, F)
                8 -> listOf(QF, SF, F)
                else -> listOf(R32, R16, QF, SF, F)
            }
        }
    }
}

/**
 * Draw status
 */
enum class DrawStatus {
    UPCOMING,
    IN_PROGRESS,
    COMPLETED;

    companion object {
        fun fromApiValue(value: Int): DrawStatus {
            return when (value) {
                1 -> UPCOMING
                2 -> IN_PROGRESS
                3 -> COMPLETED
                else -> UPCOMING
            }
        }
    }
}

/**
 * Bracket challenge status
 */
enum class BracketChallengeStatus {
    UPCOMING,
    LIVE,
    COMPLETED,
    CANCELLED,
    PAYING_OUT;

    companion object {
        fun fromApiValue(value: Int): BracketChallengeStatus {
            return when (value) {
                1 -> UPCOMING
                2 -> LIVE
                3 -> COMPLETED
                4 -> CANCELLED
                5 -> PAYING_OUT
                else -> UPCOMING
            }
        }
    }
}

/**
 * Bracket entry status
 */
enum class BracketEntryStatus {
    PENDING,
    SUBMITTED,
    LIVE,
    FINISHED,
    CANCELLED,
    PAID;

    companion object {
        fun fromApiValue(value: Int): BracketEntryStatus {
            return when (value) {
                1 -> PENDING
                2 -> SUBMITTED
                3 -> LIVE
                4 -> FINISHED
                5 -> CANCELLED
                6 -> PAID
                else -> PENDING
            }
        }
    }
}

/**
 * Lock override for admin manual control
 * NONE = Use time-based lock (default)
 * FORCE_UNLOCKED = Admin unlocked, users can submit picks
 * FORCE_LOCKED = Admin locked, users cannot submit picks
 */
enum class LockOverride {
    NONE,
    FORCE_UNLOCKED,
    FORCE_LOCKED;

    companion object {
        fun fromApiValue(value: Int): LockOverride {
            return when (value) {
                2 -> FORCE_UNLOCKED
                3 -> FORCE_LOCKED
                else -> NONE
            }
        }
    }
}

/**
 * Tournament Draw Entity
 */
@Serializable
data class TournamentDrawEntity(
    val id: String,
    val externalId: String,
    val tournamentId: String,
    val tournamentName: String,
    val seasonId: String,
    val gender: String, // "M" or "F"
    val drawSize: Int, // 32, 64, 128
    val status: DrawStatus,
    val seedingComplete: Boolean,
    val startDate: String?,
    val endDate: String?,
    val surface: String?, // hard, clay, grass
    val bestOf: Int // 3 or 5
) {
    val maxPoints: Int get() = calculateMaxPoints()

    private fun calculateMaxPoints(): Int {
        // Progressive scoring: R128=1, R64=1, R32=2, R16=4, QF=8, SF=16, F=32
        return when (drawSize) {
            128 -> 192 // 64*1 + 32*1 + 16*2 + 8*4 + 4*8 + 2*16 + 1*32
            64 -> 160  // 32*1 + 16*2 + 8*4 + 4*8 + 2*16 + 1*32
            32 -> 96   // 16*2 + 8*4 + 4*8 + 2*16 + 1*32
            16 -> 60   // 8*4 + 4*8 + 2*16 + 1*32
            else -> 96
        }
    }

    val isMens: Boolean get() = gender == "M"
    val isWomens: Boolean get() = gender == "F"
    val isGrandSlam: Boolean get() = drawSize == 128
}

/**
 * Draw Position Entity
 * Represents a single position/slot in the bracket
 */
@Serializable
data class DrawPositionEntity(
    val id: String,
    val drawId: String,
    val round: RoundName,
    val position: Int,
    val matchNumber: Int,
    val playerId: String?,
    val playerName: String?,
    val playerCountry: String?,
    val seed: Int?,
    val isBye: Boolean,
    val isQualifier: Boolean,
    val isWildcard: Boolean,
    val isLuckyLoser: Boolean,
    val matchId: String?,
    val opponentPositionId: String?,
    val winnerId: String?,
    val matchCompleted: Boolean,
    val score: String?
) {
    val hasPlayer: Boolean get() = playerId != null
    val isWinner: Boolean get() = matchCompleted && winnerId == playerId
    val isLoser: Boolean get() = matchCompleted && winnerId != null && winnerId != playerId

    val displaySeed: String?
        get() = seed?.let { "[$it]" }

    val displayName: String
        get() = when {
            isBye -> "BYE"
            isQualifier && playerName == null -> "Qualifier"
            playerName != null -> playerName
            else -> "TBD"
        }
}

/**
 * Bracket Match Entity
 * A match between two draw positions
 */
@Serializable
data class BracketMatchEntity(
    val id: String,
    val matchNumber: Int,
    val round: RoundName,
    val player1: DrawPositionEntity,
    val player2: DrawPositionEntity,
    val winnerId: String?,
    val score: String?,
    val status: String, // not_started, live, ended
    val scheduledTime: String?
) {
    val isComplete: Boolean get() = status == "ended"
    val isLive: Boolean get() = status == "live"
    val isUpcoming: Boolean get() = status == "not_started"

    val winner: DrawPositionEntity?
        get() = when (winnerId) {
            player1.playerId -> player1
            player2.playerId -> player2
            else -> null
        }
}

/**
 * Scoring Rules Configuration
 */
@Serializable
data class ScoringRulesEntity(
    val roundPoints: Map<String, Int>,
    val upsetBonusEnabled: Boolean,
    val upsetBonusMultiplier: Double,
    val bestOf3Modifier: Double
) {
    fun getPointsForRound(round: RoundName): Int {
        return roundPoints[round.name] ?: round.basePoints
    }

    companion object {
        val DEFAULT = ScoringRulesEntity(
            roundPoints = mapOf(
                "R128" to 1,
                "R64" to 1,
                "R32" to 2,
                "R16" to 4,
                "QF" to 8,
                "SF" to 16,
                "F" to 32
            ),
            upsetBonusEnabled = true,
            upsetBonusMultiplier = 0.5,
            bestOf3Modifier = 1.25
        )
    }
}

/**
 * Payout Tier
 */
@Serializable
data class PayoutTierEntity(
    val minRank: Int,
    val maxRank: Int,
    val amount: Int?, // In cents
    val percentage: Double?
)

/**
 * Bracket Challenge Entity
 */
@Serializable
data class BracketChallengeEntity(
    val id: String,
    val name: String,
    val description: String?,
    val drawId: String,
    val tournamentName: String,
    val entryFee: Int, // In cents
    val prizePool: Int, // In cents
    val maxEntries: Int?,
    val currentEntries: Int,
    val maxEntriesPerUser: Int,
    val lockTime: String, // ISO timestamp
    val status: BracketChallengeStatus,
    val scoringRules: ScoringRulesEntity,
    val payoutStructure: List<PayoutTierEntity>,
    val isFeatured: Boolean,
    val createdAt: String,
    // Lock override fields for admin manual control
    val lockOverride: LockOverride = LockOverride.NONE,
    val lockOverrideBy: String? = null,
    val lockOverrideAt: String? = null,
    val lockOverrideReason: String? = null
) {
    val isFull: Boolean get() = maxEntries != null && maxEntries > 0 && currentEntries >= maxEntries
    val spotsRemaining: Int? get() = maxEntries?.let { it - currentEntries }

    val entryFeeDisplay: String
        get() = if (entryFee == 0) "FREE" else "$${entryFee / 100.0}"

    val prizePoolDisplay: String
        get() = "$${prizePool / 100.0}"

    /**
     * Determines if the challenge is locked for submissions.
     * Admin override takes precedence over time-based lock.
     */
    val isLocked: Boolean
        get() = when (lockOverride) {
            LockOverride.FORCE_UNLOCKED -> false
            LockOverride.FORCE_LOCKED -> true
            LockOverride.NONE -> try {
                kotlinx.datetime.Clock.System.now() >= kotlinx.datetime.Instant.parse(lockTime)
            } catch (e: Exception) {
                false
            }
        }

    /** True if an admin has manually unlocked this challenge */
    val isManuallyUnlocked: Boolean get() = lockOverride == LockOverride.FORCE_UNLOCKED

    /** True if an admin has manually locked this challenge */
    val isManuallyLocked: Boolean get() = lockOverride == LockOverride.FORCE_LOCKED

    /** True if there is any lock override (not using time-based logic) */
    val hasLockOverride: Boolean get() = lockOverride != LockOverride.NONE

    val timeUntilLock: kotlin.time.Duration?
        get() = try {
            val lockInstant = kotlinx.datetime.Instant.parse(lockTime)
            val now = kotlinx.datetime.Clock.System.now()
            if (now < lockInstant) (lockInstant - now) else null
        } catch (e: Exception) {
            null
        }
}

/**
 * Bracket Entry Entity
 * User's bracket submission
 */
@Serializable
data class BracketEntryEntity(
    val id: String,
    val challengeId: String,
    val userId: String,
    val bracketName: String?,
    val totalPoints: Double,
    val correctPicks: Int,
    val incorrectPicks: Int,
    val pendingPicks: Int,
    val possiblePoints: Double,
    val rank: Int?,
    val payoutAmount: Int, // In cents
    val status: BracketEntryStatus,
    val submittedAt: String?,
    val createdAt: String,
    // User profile info for leaderboard display
    val username: String? = null,
    val avatarUrl: String? = null
) {
    val isComplete: Boolean get() = pendingPicks == 0
    val isEditable: Boolean get() = status == BracketEntryStatus.PENDING
    val winPercentage: Double
        get() {
            val total = correctPicks + incorrectPicks
            return if (total == 0) 0.0 else (correctPicks.toDouble() / total) * 100
        }

    val displayName: String get() = username?.let { "@$it" } ?: bracketName ?: "My Bracket"

    val initials: String get() = when {
        username != null -> username.take(2).uppercase()
        bracketName != null -> bracketName.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
        else -> "?"
    }
}

/**
 * Bracket Pick Entity
 * Individual match prediction
 */
@Serializable
data class BracketPickEntity(
    val id: String,
    val entryId: String,
    val positionId: String,
    val round: RoundName,
    val matchNumber: Int,
    val predictedWinnerId: String,
    val predictedWinnerName: String,
    val predictedWinnerSeed: Int?,
    val opponentId: String?,
    val opponentName: String?,
    val opponentSeed: Int?,
    val matchId: String?,
    val actualWinnerId: String?,
    val isCorrect: Boolean?,
    val pointsEarned: Double,
    val isUpsetPick: Boolean,
    val upsetBonus: Double
) {
    val isPending: Boolean get() = isCorrect == null
    val totalPoints: Double get() = pointsEarned + upsetBonus
}

/**
 * Bracket Round with matches
 */
@Serializable
data class BracketRoundEntity(
    val name: RoundName,
    val matches: List<BracketMatchEntity>
) {
    val isComplete: Boolean get() = matches.all { it.isComplete }
    val hasLiveMatches: Boolean get() = matches.any { it.isLive }
    val matchCount: Int get() = matches.size
}

/**
 * Full bracket with all rounds
 */
@Serializable
data class FullBracketEntity(
    val draw: TournamentDrawEntity,
    val rounds: List<BracketRoundEntity>,
    val positions: List<DrawPositionEntity>
)
